import { useState, useCallback, useEffect, useMemo } from 'react';
import useSWR, { mutate } from 'swr';
import { toast } from 'react-toastify';
import * as dicionarioService from '../services/dicionario';

/**
 * Hook customizado para gerenciamento de dicionários
 * Encapsula lógica de CRUD, navegação e mapeamento de campos
 *
 * @param {string} tipoExame - Tipo de exame selecionado (eco, ergo, carotida)
 * @param {string} categoria - Categoria selecionada dentro do tipo
 */
export default function useDicionario(tipoExame, categoria) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [formData, setFormData] = useState({ codigo: '', descricao: '' });
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: tipos, error: tiposError, isLoading: tiposLoading } = useSWR(
    'dicionario-tipos',
    dicionarioService.getDicionarioTipos,
    {
      revalidateOnFocus: false,
      onError: err => console.error('Erro ao buscar tipos:', err),
    }
  );

  const {
    data: categorias,
    error: categoriasError,
    isLoading: categoriasLoading,
  } = useSWR(
    tipoExame ? `dicionario-categorias-${tipoExame}` : null,
    () => dicionarioService.getDicionarioCategorias(tipoExame),
    {
      revalidateOnFocus: false,
      onError: err => console.error('Erro ao buscar categorias:', err),
    }
  );

  const { data: itensRaw, error: itensError, isLoading: itensLoading } = useSWR(
    tipoExame && categoria ? `dicionario-${tipoExame}-${categoria}` : null,
    () => dicionarioService.getDicionarioItens(tipoExame, categoria),
    {
      revalidateOnFocus: false,
      onError: err => console.error('Erro ao buscar itens:', err),
    }
  );

  const tiposMapeados = useMemo(() => {
    if (!tipos) return [];
    return tipos.map(tipo => ({
      id: tipo.codigo,
      nome: tipo.descricao,
    }));
  }, [tipos]);

  const categoriasMapeadas = useMemo(() => {
    if (!categorias) return [];
    return categorias.map(cat => ({
      id: cat.codigo,
      nome: cat.descricao,
      tabela: cat.tabela,
    }));
  }, [categorias]);

  const itens = useMemo(() => {
    if (!itensRaw) return [];
    return itensRaw.map(item =>
      dicionarioService.mapItemToFrontend(item, tipoExame, categoria)
    );
  }, [itensRaw, tipoExame, categoria]);

  // Atualiza formData quando muda a seleção ou os itens
  useEffect(() => {
    if (itens.length > 0 && !isCreating) {
      const index = Math.min(selectedIndex, itens.length - 1);
      if (index !== selectedIndex) {
        setSelectedIndex(index);
      }
      const item = itens[index];
      if (item) {
        setFormData(prev => {
          // Só atualiza se realmente mudou para evitar re-renders
          if (
            prev.codigo !== item.codigo ||
            prev.descricao !== item.descricao
          ) {
            return {
              codigo: item.codigo || '',
              descricao: item.descricao || '',
            };
          }
          return prev;
        });
      }
    } else if (itens.length === 0 && !isCreating) {
      setFormData(prev => {
        if (prev.codigo !== '' || prev.descricao !== '') {
          return { codigo: '', descricao: '' };
        }
        return prev;
      });
    }
  }, [itens, selectedIndex, isCreating]);

  // Reset ao mudar categoria
  useEffect(() => {
    setSelectedIndex(0);
    setIsCreating(false);
    setIsEditing(false);
  }, [tipoExame, categoria]);

  const goFirst = useCallback(() => {
    if (itens.length > 0) {
      setSelectedIndex(0);
      setIsCreating(false);
      setIsEditing(false);
    }
  }, [itens.length]);

  const goPrevious = useCallback(() => {
    setSelectedIndex(prev => {
      const newIndex = Math.max(0, prev - 1);
      return newIndex;
    });
    setIsCreating(false);
    setIsEditing(false);
  }, []);

  const goNext = useCallback(() => {
    setSelectedIndex(prev => {
      const newIndex = Math.min((itens?.length || 1) - 1, prev + 1);
      return newIndex;
    });
    setIsCreating(false);
    setIsEditing(false);
  }, [itens?.length]);

  const goLast = useCallback(() => {
    if (itens.length > 0) {
      setSelectedIndex(itens.length - 1);
      setIsCreating(false);
      setIsEditing(false);
    }
  }, [itens.length]);

  // Seleciona item pelo índice (ao clicar na tabela)
  const selectItem = useCallback(index => {
    setSelectedIndex(index);
    setIsCreating(false);
    setIsEditing(false);
  }, []);

  // Novo item
  const handleNew = useCallback(async () => {
    try {
      const proximoCodigo = await dicionarioService.getProximoCodigo(
        tipoExame,
        categoria
      );
      setFormData({ codigo: proximoCodigo, descricao: '' });
      setIsCreating(true);
      setIsEditing(true);
    } catch (error) {
      // Se backend não disponível, calcula localmente
      const maxCodigo = itens.reduce(
        (max, item) => Math.max(max, Number(item.codigo) || 0),
        0
      );
      setFormData({ codigo: maxCodigo + 1, descricao: '' });
      setIsCreating(true);
      setIsEditing(true);
    }
  }, [tipoExame, categoria, itens]);

  // Editar item atual
  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  // Salvar (criar ou atualizar)
  const handleSave = useCallback(async () => {
    if (!formData.descricao?.trim()) {
      toast.warning('Preencha a descrição!');
      return;
    }

    setIsSaving(true);
    try {
      const itemBackend = dicionarioService.mapItemToBackend(
        formData,
        tipoExame,
        categoria
      );

      if (isCreating) {
        await dicionarioService.createDicionarioItem(
          tipoExame,
          categoria,
          itemBackend
        );
        toast.success('Item criado com sucesso!');
      } else {
        await dicionarioService.updateDicionarioItem(
          tipoExame,
          categoria,
          formData.codigo,
          itemBackend
        );
        toast.success('Item atualizado com sucesso!');
      }

      // Revalida cache
      mutate(`dicionario-${tipoExame}-${categoria}`);
      setIsCreating(false);
      setIsEditing(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Erro ao salvar. Tente novamente.'
      );
    } finally {
      setIsSaving(false);
    }
  }, [formData, tipoExame, categoria, isCreating]);

  const handleDeleteRequest = useCallback(() => {
    if (!formData.codigo) {
      toast.warning('Selecione um item para excluir!');
      return;
    }
    setShowDeleteConfirm(true);
  }, [formData.codigo]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    setIsSaving(true);
    try {
      await dicionarioService.deleteDicionarioItem(
        tipoExame,
        categoria,
        formData.codigo
      );
      toast.success('Item excluído com sucesso!');

      // Revalida cache
      mutate(`dicionario-${tipoExame}-${categoria}`);

      // Ajusta índice selecionado
      setSelectedIndex(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Erro ao excluir. Tente novamente.'
      );
    } finally {
      setIsSaving(false);
      setShowDeleteConfirm(false);
    }
  }, [formData.codigo, tipoExame, categoria]);

  const handleCancel = useCallback(() => {
    setIsCreating(false);
    setIsEditing(false);

    // Restaura dados do item selecionado
    if (itens.length > 0 && itens[selectedIndex]) {
      setFormData({
        codigo: itens[selectedIndex].codigo,
        descricao: itens[selectedIndex].descricao,
      });
    } else {
      setFormData({ codigo: '', descricao: '' });
    }
  }, [itens, selectedIndex]);

  return {
    // Dados (mapeados para formato do componente)
    tipos: tiposMapeados,
    categorias: categoriasMapeadas,
    itens,

    // Estados de loading/error
    isLoading: tiposLoading || categoriasLoading || itensLoading,
    isSaving,
    error: tiposError || categoriasError || itensError,

    // Estado de seleção
    selectedIndex,
    selectItem,

    // Estado do formulário
    formData,
    setFormData,
    isCreating,
    isEditing,

    // Navegação
    goFirst,
    goPrevious,
    goNext,
    goLast,

    // CRUD
    handleNew,
    handleEdit,
    handleSave,
    handleDelete: handleDeleteRequest, // Abre modal de confirmação
    handleCancel,

    // Modal de confirmação de exclusão
    showDeleteConfirm,
    handleDeleteCancel,
    handleDeleteConfirm,
  };
}
